/**
 * Odds-filter chip trio — U | OO | OP per-source visibility for the
 * comparison columns (Dan 2026-08-23; placement revised same day: lives
 * DISCREETLY in the table's own title bar, not the global toolbar — the
 * chips govern THIS table's columns, and the toolbar is board-global
 * controls. Styled small + quiet on purpose: an advanced-user control).
 *
 * Self-contained: reads/writes displaySettings directly so any hosting
 * table gets the control without prop plumbing. The SETTINGS remain global
 * — flipping a chip here changes every odds surface, exactly as before.
 */
import { cn } from '@/lib/infra/utils';
import { useDisplaySettings } from '@/stores/displaySettings';

export function OddsFilterChips() {
  const showUnbtdOdds = useDisplaySettings((s) => s.showUnbtdOdds);
  const showOoOdds = useDisplaySettings((s) => s.showOoOdds);
  const showOpOdds = useDisplaySettings((s) => s.showOpOdds);
  const setShowUnbtdOdds = useDisplaySettings((s) => s.setShowUnbtdOdds);
  const setShowOoOdds = useDisplaySettings((s) => s.setShowOoOdds);
  const setShowOpOdds = useDisplaySettings((s) => s.setShowOpOdds);
  const chip = (
    label: string,
    on: boolean,
    toggle: () => void,
    title: string,
  ) => (
    <button
      type="button"
      onClick={toggle}
      aria-pressed={on}
      title={title}
      className={cn(
        'rounded px-1.5 py-0.5 text-[10px] font-semibold transition-colors',
        on
          ? 'bg-primary/80 text-primary-foreground'
          : 'text-muted-foreground/60 hover:text-foreground',
      )}
    >
      {label}
    </button>
  );
  return (
    <div className="flex items-center gap-1" aria-label="Odds columns">
      <span className="text-muted-foreground/50 pr-0.5 text-[9px] font-semibold uppercase tracking-wide">
        Odds
      </span>
      {chip('U', showUnbtdOdds, () => setShowUnbtdOdds(!showUnbtdOdds),
        'Unabated consensus column. Off by default — Unabated is being phased out; OddsPapi is the going-forward comparison feed.')}
      {chip('OO', showOoOdds, () => setShowOoOdds(!showOoOdds),
        'OpticOdds (trial) column. Off by default; even when on it only appears for sports the trial serves (MLB, NFL, cricket). OO and OP consensus use different book sets by design — compare the Pinnacle line, not the consensus.')}
      {chip('OP', showOpOdds, () => setShowOpOdds(!showOpOdds),
        'OddsPapi comparison column (OP/PNCL) — de-vigged consensus / Pinnacle fair values.')}
    </div>
  );
}
